/**
 * Live PowerReviews rollup store (SFCC-11800).
 *
 * The SSR'd header count comes from the catalog attr `c_totalReviews`, which
 * the SFRA import job only ever UPserts — products whose reviews drop out of
 * PowerReviews keep a stale count forever (41866: attr says 83, PR has 0).
 * The SDK's `on_render` reports the live rollup per component (verified
 * against ui.js 4.1: ReviewSnippet gets `{review_count, average_rating}`,
 * and fires with EMPTY data for a 0-review product), so the header re-syncs
 * to PR truth once the widget renders. Until the rollup lands the header
 * shows a skeleton; on SDK failure (script error) or timeout it falls back
 * to the attr values, and a late rollup still takes over.
 */
import { useSyncExternalStore } from "react";

export interface PowerReviewsRollup {
  reviewCount: number;
  averageRating: number;
}

// Client-session-scoped and tiny (one ~50-byte entry per distinct master id
// visited) — deliberately no eviction.
const rollups = new Map<string, PowerReviewsRollup>();
const listeners = new Set<() => void>();

export function setPowerReviewsRollup(
  pageId: string,
  rollup: PowerReviewsRollup,
) {
  const prev = rollups.get(pageId);
  if (
    prev &&
    prev.reviewCount === rollup.reviewCount &&
    prev.averageRating === rollup.averageRating
  )
    return;
  rollups.set(pageId, rollup);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Live PowerReviews rollup for a page_id, or undefined until the SDK reports. */
export function usePowerReviewsRollup(
  pageId: string,
): PowerReviewsRollup | undefined {
  return useSyncExternalStore(
    subscribe,
    () => rollups.get(pageId),
    () => undefined,
  );
}

// SDK script load failure. Snapshot state (not an event) so a header that
// mounts after the error still reads it. Set-only in prod — the dead <script>
// stays in the DOM and bootSdk's querySelector guard never re-attempts, so
// later PDPs this session skip straight to the attr fallback. The `false`
// param exists for test reset.
let sdkFailed = false;

export function setPowerReviewsSdkFailed(failed: boolean) {
  if (sdkFailed === failed) return;
  sdkFailed = failed;
  for (const listener of listeners) listener();
}

export function usePowerReviewsSdkFailed(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => sdkFailed,
    () => false,
  );
}
